<template>
  <div
      class="chat-message mb-3" v-if="!message.deleted"
      v-bind:id="'chat-message-' + message.id"
  >
    <div class="d-flex flex-row">
      <div class="chat-message__username-offset mr-3"></div>
      <div class="chat-message__username mb-1 grey--text pl-3">
        <a target="_blank" rel="noopener noreferrer"
           v-bind:href=getUserProfileLink()
           :title=message.sender.author>
          {{ message.sender.author }}
        </a>
      </div>
    </div>
    <div class="d-flex flex-row">
      <div class="chat-message__user-picture grey lighten-3 mr-3">
        <img
            v-if="getAuthorAvatar(message)"
            v-bind:src=getAuthorAvatar(message)
            alt="">
        <v-icon
            v-if="!getAuthorAvatar(message)"
            color="grey"
            size="36px"
        >
          mdi-account
        </v-icon>
      </div>
      <div
          v-bind:class="{ 'non-own-message': !isMe, 'own-message': (isMe && !replaceSticker(message.msg)), 'sticker-message': replaceSticker(message.msg)}"
          class="pa-3 grey lighten-3 chat-message__message-body flex-fill pb-10"
      >
        <v-btn v-if="!isMe && !amIModerator"
               dark
               icon
               absolute="absolute"
               @click="onAnswer(message)"
               class="chat-message__message-menu"
        >
          <v-icon
              color="grey"
              size="22px"
          >
            mdi-reply
          </v-icon>
        </v-btn>
        <v-btn v-if="isMe || amIModerator"
               dark
               icon
               class="chat-message__message-menu"
               @click.prevent.stop="!isMenuOpened ? $refs.menu.open($event) : $refs.menu.close() && $refs.menu.open($event)"
        >
          <v-icon
              color="grey"
              size="32px"
          >
            mdi-chevron-down
          </v-icon>
        </v-btn>
        <div class="chat-message__quote" v-if="message.quote" @click="scrollToQuoteMsg(message.quote)">
          <div class="chat-message__quote-author grey--text">{{ message.quote.author }}</div>
          <img v-if="replaceSticker(message.quote.msg)" :src="replaceSticker(message.quote.msg)"/>
          <nl2br v-else tag="div" :text="cleanMessage(message.quote.msg)" class-name="chat-message__quote-body"/>
        </div>

        <img v-if="replaceSticker(message.msg)" :src="replaceSticker(message.msg)"/>
        <span v-else v-html="prepareMessage(message.msg)" class="chat-message__message-body-text"></span>
        <span class="chat-message__message-like">
            <span v-if="message.liked" @click="onDislike(message)">
              <v-icon size="18px">mdi-heart</v-icon>
            </span>
            <span v-else @click="onLike(message)">
              <v-icon size="18px">mdi-heart-outline</v-icon>
            </span>
            {{ message.likes }}
          </span>
        <span
            class="chat-message__message-status grey--text"
        >
          {{ showTime() }}
        </span>
      </div>
      <vue-context ref="menu" v-on:open="toggleMenuOpened(true)"
                   v-on:close="toggleMenuOpened(false)">
        <li v-for="(item, i) in this.menuItems" :key="i">
          <a href="#" @click.prevent="item.onClick(message)">{{ item.title }}</a>
        </li>
      </vue-context>
    </div>
  </div>
</template>

<script>
import VueContext from 'vue-context';
import {localize} from "@/store/modules/localization";
import {cleanMessage, prepareMessage, replaceSticker} from "@/helpers";

export default {
  name: 'Message',
  components: {VueContext},
  props: {
    'message': Object,
    'onAnswer': {
      type: Function,
      default: () => {
      }
    },
    'onDelete': {
      type: Function,
      default: () => {
      }
    },
    'onMarkQuestion': {
      type: Function,
      default: () => {
      }
    },
    'onUnmarkQuestion': {
      type: Function,
      default: () => {
      }
    },
    'onMarkAttention': {
      type: Function,
      default: () => {
      }
    },
    'onUnmarkAttention': {
      type: Function,
      default: () => {
      }
    },
    'onBan': {
      type: Function,
      default: () => {
      }
    },
    'onLike': {
      type: Function,
      default: () => {
      }
    },
    'onDislike': {
      type: Function,
      default: () => {
      }
    }
  },
  data: function () {
    return {
      isMe: false,
      menuOpened: false,
      openToTop: false,
      positionX: 0,
      positionY: 0,
    }
  },
  computed: {
    amIModerator() {
      return this.$store.getters.amIModerator
    },
    amIGuest() {
      return this.$store.getters.amIGuest
    },
    getChatMsgId() {
      return `#chat-message-${this.message.id}`
    },
    isMenuOpened() {
      return this.$store.getters.getMenuOpened
    },
    menuItems() {
      return this.buildMenu()
    }
  },
  created() {
    this.isMe = this.message.sender.uid === this.$store.getters.getMyId
  },
  methods: {
    buildMenu() {
      var menuItems = [
        {title: localize('Answer', this.$settings.lang), onClick: this.onAnswer},
      ]
      if (!this.amIGuest) menuItems.push({title: localize('Delete', this.$settings.lang), onClick: this.onDelete});
      if (this.amIModerator) {
        if (this.message.isQuestion)
          menuItems.push({
            title: localize('This is not a question', this.$settings.lang),
            onClick: this.onUnmarkQuestion
          });
        else
          menuItems.push({title: localize('This is a question', this.$settings.lang), onClick: this.onMarkQuestion});
        if (this.message.isImportant)
          menuItems.push({title: localize('Unimportant message', this.$settings.lang), onClick: this.onUnmarkAttention})
        else
          menuItems.push({title: localize('Important message', this.$settings.lang), onClick: this.onMarkAttention})
        menuItems.push({title: localize('Ban', this.$settings.lang), onClick: this.onBan})
      }
      return menuItems
    },
    showTime() {
      const date = new Date();
      date.setTime(this.message.timestamp * 1000)
      const dateTimeFormat = new Intl.DateTimeFormat('ru', {
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      const [{value: day}, , {value: month}, , {value: hour}, , {value: minute}] = dateTimeFormat.formatToParts(date)
      return `${day} ${month} ${hour}:${minute}`
    },
    getAuthorAvatar(msg) {
      if (msg.sender.avatar) return msg.sender.avatar

      const user = this.$store.getters.getUserById(this.message.sender.uid)
      if (user) return user.avatar
    },
    getUserProfileLink() {
      return `${this.$settings.domainprofilelink}/${this.message.sender.uid}`
    },
    toggleMenuOpened(value) {
      this.menuOpened = value
      this.$store.dispatch('setMenuOpened', value)
    },
    scrollToQuoteMsg(quote) {
      const el = document.querySelector(`#chat-message-${quote.id}`);
      el.scrollIntoView({behavior: 'auto', block: 'nearest', inline: 'end'})
    },
    replaceSticker(msg) {
      const wsUrl = this.$settings.websocket || this.$store.getters.getServer
      const host = wsUrl.replace('ws://', 'http://').replace('wss://', 'https://')
      const src = new URL('/static/stickers/shark/', host).href
      return replaceSticker(msg, src);
    },
    prepareMessage(msg) {
      return prepareMessage(msg);
    },
    cleanMessage(msg) {
      return cleanMessage(msg);
    }
  }
}
</script>

<style>
@import '~vue-context/dist/css/vue-context.css';
</style>
